import { useState, useEffect } from 'react'
import Stack from '@mui/material/Stack'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Checkbox from '@mui/material/Checkbox'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

import IconButtonOutlined from './IconButtonOutlined'

function not(a: readonly number[], b: readonly number[]) {
  return a.filter(value => !b.includes(value))
}

function intersection(a: readonly number[], b: readonly number[]) {
  return a.filter(value => b.includes(value))
}

interface TransferListProps<T extends Record<string, unknown>> {
  data: T[]
  selected: readonly number[]
  excludeData: readonly number[]
  setExcludeData: (value: readonly number[]) => void
  selectionGetter: (item: T) => number
  renderOption: (data: T) => React.ReactNode
  sortGetter: (item: T) => number
  onUp: (id: number) => Promise<void>
  onDown: (id: number) => Promise<void>
  onRight?: (selected: readonly number[]) => Promise<readonly number[]>
  onLeft?: (selected: readonly number[]) => Promise<readonly number[]>
  height?: string | number
  filterData?: (data: T) => boolean
}

export default function TransferList<T extends Record<string, unknown>>({
  data,
  excludeData,
  setExcludeData,
  selected,
  selectionGetter,
  renderOption,
  onRight,
  onLeft,
  height = 230,
  filterData = () => true,
  sortGetter,
  onUp,
  onDown,
}: TransferListProps<T>) {
  const [loading, setLoading] = useState(false)
  const [checked, setChecked] = useState<readonly number[]>([])
  const [left, setLeft] = useState<readonly number[]>([])
  const [right, setRight] = useState<readonly number[]>([])

  useEffect(() => {
    setLeft(
      data
        .filter(
          el =>
            !selected.includes(selectionGetter(el)) && !excludeData.includes(selectionGetter(el))
        )
        .filter(filterData)
        .map(selectionGetter)
    )
    setRight(selected)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  useEffect(() => {
    setLeft(
      data
        .filter(
          el => !right.includes(selectionGetter(el)) && !excludeData.includes(selectionGetter(el))
        )
        .filter(filterData)
        .map(selectionGetter)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeData, filterData])

  const leftChecked = intersection(checked, left)
  const rightChecked = intersection(checked, right)

  const handleToggle = (value: number) => () => {
    const currentIndex = checked.indexOf(value)
    const newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    setChecked(newChecked)
  }

  const handleAllRight = () => {
    const newExcludedData = excludeData.concat(left)
    const newRight = right.concat(left)
    setRight(newRight)
    setLeft([])
    setExcludeData(newExcludedData)

    if (onRight) {
      setLoading(true)
      onRight(left)
        .then(rejected => {
          setLoading(false)
          if (rejected.length === 0) return
          setLeft(rejected)
          setRight(not(newRight, rejected))
          setExcludeData(not(newExcludedData, rejected))
        })
        .catch(() => {
          setLoading(false)
        })
    }
  }

  const handleCheckedRight = () => {
    const newExcludedData = excludeData.concat(leftChecked)
    const newRight = right.concat(leftChecked)
    const newLeft = not(left, leftChecked)
    setRight(newRight)
    setLeft(newLeft)
    setChecked(not(checked, leftChecked))
    setExcludeData(newExcludedData)

    if (onRight) {
      setLoading(true)
      onRight(leftChecked)
        .then(rejected => {
          setLoading(false)
          if (rejected.length === 0) return
          setLeft(newLeft.concat(rejected))
          setRight(not(newRight, rejected))
          setExcludeData(not(newExcludedData, rejected))
        })
        .catch(() => {
          setLoading(false)
        })
    }
  }

  const handleCheckedLeft = () => {
    const newLeft = left.concat(rightChecked)
    const newRight = not(right, rightChecked)
    const newExcludeData = not(excludeData, rightChecked)
    setLeft(newLeft)
    setRight(newRight)
    setChecked(not(checked, rightChecked))
    setExcludeData(newExcludeData)

    if (onLeft) {
      setLoading(true)
      onLeft(rightChecked)
        .then(rejected => {
          setLoading(false)
          if (rejected.length === 0) return
          setRight(newRight.concat(rejected))
          setLeft(not(newLeft, rejected))
          setExcludeData(newExcludeData.concat(rejected))
        })
        .catch(() => {
          setLoading(false)
        })
    }
  }

  const handleAllLeft = () => {
    const newLeft = left.concat(right)
    const newExcludeData = not(excludeData, right)
    setLeft(newLeft)
    setRight([])
    setExcludeData(newExcludeData)

    if (onLeft) {
      setLoading(true)
      onLeft(right)
        .then(rejected => {
          setLoading(false)
          if (rejected.length === 0) return
          setRight(rejected)
          setLeft(not(newLeft, rejected))
          setExcludeData(newExcludeData.concat(rejected))
        })
        .catch(() => {
          setLoading(false)
        })
    }
  }

  const customList = (items: readonly number[], canOrder?: boolean) => (
    <Paper
      sx={theme => ({
        backgroundColor: theme.colors.secondaryBackground,
        width: '100%',
        height,
        overflow: 'auto',
      })}
    >
      <List dense component="div" role="list">
        {[...items]
          .sort((a, b) => {
            if (!sortGetter) return 0
            const aData = data.find(el => selectionGetter(el) === a)
            const bData = data.find(el => selectionGetter(el) === b)
            if (!aData || !bData) return 0
            if (sortGetter(bData) === 0) return -1
            // if (sortGetter(aData) === 0) return 1
            return sortGetter(aData) - sortGetter(bData)
          })
          .map((value: number) => {
            const labelId = `transfer-list-item-${value}-label`
            const elData = data.find(el => selectionGetter(el) === value)
            if (!elData) return null

            return (
              <ListItemButton
                key={value}
                role="listitem"
                onClick={handleToggle(value)}
                sx={{ margin: '10px 0', height: 'auto' }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={checked.includes(value)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId}>{renderOption(elData)}</ListItemText>
                {canOrder && (
                  <Stack>
                    <IconButtonOutlined
                      size="small"
                      sx={{ p: 0 }}
                      disabled={loading}
                      loading={loading}
                      onClick={event => {
                        event.stopPropagation()
                        setLoading(true)
                        onUp(value)
                          .then(() => {
                            setLoading(false)
                          })
                          .catch(() => {
                            setLoading(false)
                          })
                      }}
                    >
                      <KeyboardArrowUpIcon sx={{ opacity: loading ? 0 : 1 }} />
                    </IconButtonOutlined>
                    <IconButtonOutlined
                      size="small"
                      sx={{ p: 0 }}
                      disabled={loading}
                      loading={loading}
                      onClick={event => {
                        event.stopPropagation()
                        setLoading(true)
                        onDown(value)
                          .then(() => {
                            setLoading(false)
                          })
                          .catch(() => {
                            setLoading(false)
                          })
                      }}
                    >
                      <KeyboardArrowDownIcon sx={{ opacity: loading ? 0 : 1 }} />
                    </IconButtonOutlined>
                  </Stack>
                )}
              </ListItemButton>
            )
          })}
      </List>
    </Paper>
  )

  return (
    <Stack
      direction="row"
      spacing={2}
      padding={1}
      sx={{ justifyContent: 'center', alignItems: 'center' }}
    >
      <Stack flex="1 1">{customList(left)}</Stack>
      <Stack>
        <Stack direction="column" sx={{ alignItems: 'center' }}>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllRight}
            disabled={left.length === 0 || loading}
            loading={loading}
            aria-label="move all right"
          >
            ≫
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0 || loading}
            loading={loading}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0 || loading}
            loading={loading}
            aria-label="move selected left"
          >
            &lt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllLeft}
            disabled={right.length === 0 || loading}
            loading={loading}
            aria-label="move all left"
          >
            ≪
          </Button>
        </Stack>
      </Stack>
      <Stack flex="1 1">{customList(right, true)}</Stack>
    </Stack>
  )
}
